<template>
  <div class="home">
    <header class="site-header" :class="{ 'is-sticky': sticky === true }">
      <div class="site-header-inner">
        <scrollactive
          class="main-nav"
          active-class="is-active"
          :offset="80"
          :duration="800"
          bezier-easing-value="0.5,0,0.35,1"
        >
          <ul>
            <li v-for="item in l10n[language].menu" :key="item.id">
              <a class="scrollactive-item" :href="`/${item.url}`">{{
                item.name
              }}</a>
            </li>
            <li>
              <router-link
                :to="{
                  path: `/trailer/${l10n[language].films.button.url}`,
                  query: { lang: language }
                }"
                >{{ l10n[language].films.button.label }}</router-link
              >
            </li>
          </ul>
        </scrollactive>

        <h1 class="logo">One Day.</h1>

        <nav class="lang-nav">
          <ul>
            <li>
              <button
                :class="{ 'is-active': language === 'en' }"
                @click="changeLanguage('en')"
              >
                ENGLISH
              </button>
            </li>
            <li>
              <button
                :class="{ 'is-active': language === 'fr' }"
                @click="changeLanguage('fr')"
              >
                FRANÇAIS
              </button>
            </li>
            <li>
              <button
                :class="{ 'is-active': language === 'es' }"
                @click="changeLanguage('es')"
              >
                ESPAÑOL
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </header>

    <div class="imagery">
      <img style="top: 180px; left: 0;" src="/static/images/1.png" alt="" />
      <img style="top: 580px; right: 0;" src="/static/images/2.png" alt="" />
      <img style="top: 780px; left: 0;" src="/static/images/3.png" alt="" />
    </div>

    <div class="signatures">
      <img
        style="top: 180px; right: 20%;"
        src="/static/images/signatures/adjoujiu.png"
        alt="Adjoujiu"
      />
      <img
        style="top: 360px; right: 5%;"
        src="/static/images/signatures/demba.png"
        alt="Demba"
      />
      <img
        style="top: 620px; right: 10%;"
        src="/static/images/signatures/younous.png"
        alt="Younous"
      />
      <img
        style="top: 280px; left: 10%;"
        src="/static/images/signatures/chepito.png"
        alt="Chepito"
      />
      <img
        style="top: 560px; left: 2%;"
        src="/static/images/signatures/chelsea.png"
        alt="Chelsea"
      />
    </div>

    <section class="intro">
      <h1 class="intro__logo">One Day.</h1>
      <p class="intro__sub">{{ l10n[language].intro.series }}</p>
      <p class="intro__psi" v-html="l10n[language].intro.sponsor"></p>
      <img class="intro__image" src="@/assets/psi-logo-full.svg" alt="" />

      <div class="intro__map" v-html="worldMap"></div>
    </section>

    <section class="intro__people">
      <div
        class="block"
        v-for="(person, index) in l10n[language].people"
        :class="{
          'block--align-right': index % 2 === 0,
          'block--last': index + 1 === l10n[language].people.length
        }"
        :key="person.id"
      >
        <div class="block__inner">
          <div class="block__image">
            <img :src="person.image" alt="" />
          </div>
          <div class="block__text">
            {{ person.content }}
          </div>
        </div>
      </div>
    </section>

    <section class="red">
      <div class="red__inner">
        <div class="red__left">
          <p id="about">{{ l10n[language].about.lead }}</p>

          <img class="intro__image" src="@/assets/psi-logo-white.svg" alt="" />
        </div>

        <div class="red__right" v-html="l10n[language].about.content"></div>
      </div>
    </section>

    <section class="films">
      <header class="films__header">
        <h1 class="films__title" id="films">
          {{ l10n[language].films.title }}
        </h1>
        <p class="films__cta">
          <router-link
            :to="{
              path: `/trailer/${l10n[language].films.button.url}`,
              query: { lang: language }
            }"
            >{{ l10n[language].films.button.label }}</router-link
          >
        </p>
      </header>

      <div class="film-wrapper" v-observe-visibility="changeVisibility">
        <article
          class="film-block"
          v-for="(film, index) in l10n[language].films.list"
          :key="film.id"
          :id="`film-${index}`"
        >
          <div class="film-block__video">
            <router-link
              :to="{
                path: `/film/${film.name_slug}`,
                query: { name: film.image_slug, lang: language, id: index }
              }"
              class="film-block__image"
            >
              <img
                :src="`/static/images/thumbs/${film.image_slug}.jpg`"
                alt=""
              />
            </router-link>
            <div class="film-block__video-right">
              <h2 class="film-block__signature">
                <small>{{ film.job }}</small>
                <img
                  :src="`/static/images/signatures/${film.name_slug}.png`"
                  :alt="film.name"
                  v-if="film.name_slug"
                />
                <span v-else>{{ film.name }}</span>
              </h2>
              <p class="film-block__quote">{{ film.quote }}</p>
            </div>
          </div>

          <div class="film-block__bottom">
            <h2 class="film-block__name">{{ film.title }}</h2>
            <div class="film-block__description">
              <p>
                <strong>{{ film.country }}</strong>
              </p>
              <p>{{ film.description }}</p>

              <small>{{ film.union }}</small>
            </div>
          </div>
        </article>
      </div>

      <scrollactive
        class="films__nav"
        active-class="is-active"
        :offset="80"
        :duration="400"
        bezier-easing-value="0.5,0,0.35,1"
        v-if="l10n[language].films.list"
        :class="{ 'is-active': filmsVisible === true }"
      >
        <ul>
          <li v-for="(item, index) in l10n[language].films.list" :key="item.id">
            <a class="scrollactive-item" :href="`#film-${index}`">{{
              item.title
            }}</a>
          </li>
        </ul>
      </scrollactive>
    </section>

    <section class="credits">
      <div class="credits__inner">
        <div class="credits__left">
          <div class="credits__left-logo">
            <h2>One Day.</h2>
            <p>{{ l10n[language].intro.series }}</p>
          </div>
          <div class="credits__left-columns">
            <h2>Press Inquiries</h2>
            <p>
              <a href="mailto:communications@world-psi.org"
                >communications@world-psi.org</a
              >
            </p>
          </div>
        </div>
        <div class="credits__right">
          <div class="credits__right-column">
            <h2>{{ l10n[language].footer.production }}</h2>
            <p>
              Marcelo Netto<br />
              Leo Hyde
            </p>
            <h2>{{ l10n[language].footer.shot }}</h2>
            <p>
              Leo Hyde<br />
              Sonia Sanchez<br />
              Marcelo Netto<br />
              Igor Ojeda
            </p>
          </div>
          <div class="credits__right-column">
            <h2>{{ l10n[language].footer.thanks }}</h2>
            <p>
              Nobuko Mitsui<br />
              Carlos Verdugo<br />
              Norman Grecia<br />
              Indah Budiarti<br />
              Najwa Hanna<br />
              Charlotte Kalabani<br />
              Daria Cibrario<br />
              José Arturo Ruíz<br />
              Nayareth Quevedo<br />
              Zakaria Taleb
            </p>
          </div>
        </div>
      </div>
    </section>

    <footer class="site-footer">
      <div class="site-footer__inner">
        <img src="@/assets/psi-logo-footer.svg" alt="" />

        <div v-html="l10n[language].footer.psi_description"></div>
      </div>

      <div class="site-footer__credits">
        <p>{{ l10n[language].footer.art }}: <a href="#">Bojan Kanizaj</a></p>
        <p>2018. &copy; {{ l10n[language].footer.psi }}</p>
        <p>
          {{ l10n[language].footer.dev }}
          <a href="http://mariojankovic.com" target="_blank">Mario Jankovic</a>
        </p>
      </div>
    </footer>

    <transition name="fade">
      <router-view :l10n="l10n" />
    </transition>
  </div>
</template>

<script>
import worldMap from '@/assets/world-map-dots.js'

export default {
  name: 'Home',
  methods: {
    changeLanguage(lang) {
      this.language = lang
      this.$router.push({ query: { lang: this.language } })
    },
    changeVisibility(visibility) {
      this.filmsVisible = visibility
    }
  },
  mounted() {
    const Vue = this
    window.addEventListener('scroll', function () {
      if (this.pageYOffset > 700) {
        Vue.sticky = true
      } else {
        Vue.sticky = false
      }
    })
  },
  computed: {
    worldMap() {
      return worldMap
    }
  },
  data() {
    return {
      language: 'en',
      sticky: false,
      filmsVisible: false,
      l10n: {
        en: {
          menu: [
            { name: 'About', url: '#about' },
            { name: 'Films', url: '#films' }
          ],
          intro: {
            series: 'A SERIES OF FILMS ON THE WORLD OF WORK',
            sponsor:
              'PUBLIC SERVICES INTERNATIONAL<br />GLOBAL UNION FEDERATION'
          },
          people: [
            {
              content:
                'In Chad, Younous fights for his patients’ lives - and against the dictatorial regime.',
              image: '/static/images/4.png'
            },
            {
              content:
                'In Japan, Reina fights fires - and for her right to unionise.',
              image: '/static/images/5.png'
            },
            {
              content:
                'In Lebanon, Georges fights to bring water to his community – and for the rights of refugees.',
              image: '/static/images/6.png'
            }
          ],
          about: {
            lead:
              'WHILE THEIR STORIES MIGHT BE DIFFERENT, THE GOAL OF THESE PUBLIC WORKERS IS THE SAME: TO MAKE THEIR COMMUNITIES BETTER FOR ALL - NOT JUST THE RICH FEW.',
            content:
              '<p>One Day, produced by the global union federation Public Services International, is a series of short films which attempt to capture an ordinary day in the extraordinary lives of public service workers such as Younous, Reina and Georges.</p> <p>Featuring over a dozen workers and spanning the globe, these films from the frontline feature struggles against privatisation, austerity and neoliberalism and campaigns for trade union rights, disaster preparedness and access to quality public services.</p>'
          },
          footer: {
            production: 'Produced By',
            shot: 'Shot By',
            thanks: 'Special thanks to',
            art: 'Art direction',
            dev: 'Development',
            press: 'PRESS INQUIRIES',
            psi: 'Public Services International, all rights reserved',
            psi_description:
              '<p><strong>Public Services International</strong> is a global trade union federation representing 20 million working women and men who deliver vital public services in 163 countries.</p>'
          },
          films: {
            title: 'Films',
            button: {
              label: 'Watch trailer',
              url: '265952877'
            },
            list: [
              {
                video_id: '265558045',
                name: 'Younous',
                name_slug: 'younous',
                image_slug: 'younous',
                job: 'Doctor',
                quote:
                  '“It’s a system of torture called Arbatachar. It’s a system you don’t forget”',
                title: 'Dr. Activist',
                country: 'Chad',
                description:
                  'After helping send Chad’s former dictator to prison, Younous keeps up the struggle for trade union rights and healthcare for all in one of Africa’s most repressive regimes.',
                union:
                  'Federation des Syndicats du Secteur Public du Tchad (FSPT)'
              },
              {
                video_id: '265559904',
                name: 'Arabella',
                name_slug: 'arabella',
                image_slug: 'arabella',
                job: 'Creche Worker',
                quote:
                  '“The cry of a young child has a thousand different nuances”',
                title: 'Sound and Silence',
                country: 'Italy',
                description:
                  "Arabella gives us an insight into the patience and sensitivity required to help develop society's youngest new members.",
                union:
                  'Confederazione Generale Italiana del Lavoro,  Funzione Pubblica'
              },
              {
                video_id: '265554485',
                name: 'Negro Davi',
                name_slug: 'negro',
                image_slug: 'negro',
                job: 'Waste Worker and Organiser',
                quote:
                  '“There’s a total symbiosis between my music and my union activism”',
                title: 'We’re not trash',
                country: 'Brazil',
                description:
                  'Through his music, Negro Davi stands up for respect for the waste workers he helps to organise.',
                union: 'SINDILIMP'
              },
              {
                video_id: '265560429',
                name: 'Adjoujiu',
                name_slug: 'adjoujiu',
                image_slug: 'adjoujiu',
                job: 'Public Administrator',
                quote:
                  '“When women try to get ahead, we often find banana peels are slipped in front of us, to make us fall”',
                title: 'Chad’s Women March On',
                country: 'Chad',
                description:
                  'Follow Adjoujiu’s struggle to organize Chad’s first ever Women’s March - for the rights of workers, in the face of an oppressive regime.',
                union:
                  'Federation des Syndicats du Secteur Public du Tchad (FSPT)'
              },
              {
                video_id: '265558673',
                name: 'Chelsea',
                name_slug: 'chelsea',
                image_slug: 'chelsea',
                job: 'Correctional Officer',
                quote:
                  '“We’re not all mean and tough and roaaaaar…. We’re just not like that”',
                title: 'Showing Human Face',
                country: 'USA',
                description:
                  'In the face of public opinion, Chelsea highlights the human qualities and care for others required for her work as a prison officer.',
                union: 'AFL-CIO'
              },
              {
                video_id: '265556122',
                name: 'Georges',
                name_slug: 'georges',
                image_slug: 'georges',
                job: 'Water Worker',
                quote:
                  '“We were refugees, we lived through war, and now we can help our brothers”',
                title: 'Solidarity Across Borders',
                country: 'Lebanon',
                description:
                  'After Syrian refugees fleeing the war settle in Georges’ community, he and his union work to ensure they all have access to safe public drinking water and sanitation.',
                union:
                  'Syndicat des Employes et Ouvriers de l’Office des Eaux de Beyrouth et du Mt Liban (SEOEB)'
              },
              {
                video_id: '265557687',
                name: 'Eko',
                name_slug: 'eko',
                image_slug: 'eko',
                job: 'Electricity Worker',
                quote:
                  ' “We took our case for public electricity all the way to the supreme court”',
                title: 'Power for the people',
                country: 'Indonesia',
                description:
                  'Eko’s job at the local electricity plant comes under threat and as the government prepares for privatisation, he and his colleagues fight back.',
                union: 'Serikat Perkerja Perjuangan PT PLN Persero (SPP.PLN)'
              },
              {
                video_id: '265557112',
                name: 'Pasquale and Emmanuele',
                name_slug: 'pascale-emanuele',
                image_slug: 'pascale-emanuele',
                job: 'Waste Workers',
                quote:
                  '“In the morning, the streets are silent, calm, beautiful - we make them clean”',
                title: 'Guardians of the City',
                country: 'Italy',
                description:
                  'While tourists and residents are still asleep, Pasquale and Emmauele are already hitting the streets to help keep Siena one of the most beautiful cities in the world.',
                union:
                  'Confederazione Generale Italiana del Lavoro,  Funzione Pubblica'
              },
              {
                video_id: '265955585',
                name: 'Reina',
                name_slug: 'reina',
                image_slug: 'reina',
                job: 'Ambulance and Fire Service Worker',
                quote:
                  '“As a sick child in hospital, I dreamed of growing up and helping others.”',
                title: 'Reina’s Dream',
                country: 'Japan',
                description:
                  'As one of only four female emergency workers in her unit, Reina fights fires, saves lives and advocates for workers rights in Omuta City, Japan.',
                union: 'JICHIRO, JPSU'
              },
              {
                video_id: '265559698',
                name: 'Demba',
                name_slug: 'demba',
                image_slug: 'demba',
                job: 'Legal Aid',
                quote:
                  '“You can never leave this type of work, at work. It stays with you”',
                title: 'Justice: A public Service',
                country: 'Chad',
                description:
                  'As public legal assistant for victims of rape and abuse, Demba uses the law to fight injustices - while fighting to  maintain her hope.',
                union:
                  'Federation des Syndicats du Secteur Public du Tchad (FSPT)'
              },
              {
                video_id: '265555634',
                name: 'Ali, Kamal and Elian',
                name_slug: 'ali-kamal-elian',
                image_slug: 'ali-kamal-elian',
                job: 'Water Workers',
                quote:
                  '“When you don’t have a fixed hours, a fixed contract - you don’t have a future”',
                title: 'Flow',
                country: 'Lebanon',
                description:
                  'Workers in Tripoli fight to end the contractualisation of young comrades and extend public water service to all.',
                union:
                  'Syndicat des Employes et Ouvriers de l’Office des Eaux de Beyrouth et du Mt Liban (SEOEB) '
              },
              {
                video_id: '265560204',
                name: 'Anvic',
                name_slug: 'anvic',
                image_slug: 'anvic',
                job: 'First Responder',
                quote:
                  '“We need to invest and prepare in good times for what is coming next”',
                title: 'The Ark',
                country: 'The Philippines',
                description:
                  'When disasters strike the Philippines, Anvic is one of the brave First Responders leaving his family behind and running into the eye of the storm.',
                union:
                  'Public Services Labor Independent Confederation (PSLINK)'
              },
              {
                video_id: '265558342',
                name: 'Daksim',
                name_slug: 'daksim',
                image_slug: 'daksim',
                job: 'Water Worker',
                quote:
                  '“Not many people know but if we don’t get an effective public water system soon, Jakarta will sink into the sea.”',
                title: 'The Big Fish',
                country: 'Indonesia',
                description:
                  'After 20 years of failed water privatisation, Daksim and his union still haven’t given up the fight to bring their water supply back into the hands of the people of Jakarta',
                union:
                  'Serikat Pekerja Perusahaan Daerah Air Minum Jakarta (SP PDAM Jakarta)'
              },
              {
                video_id: '265556454',
                name: 'Chepito',
                name_slug: 'chepito',
                image_slug: 'chepito',
                job: 'Water Worker',
                quote:
                  '“Since my student days, they have been beating up. It hasn’t stopped me yet”',
                title: 'Comrade Chepito',
                country: 'Guatemala',
                description:
                  'In Guatemala where unionists face detention and even assassination, Chepito works to organise public workers and fights for the right to water for all.',
                union:
                  'Sindicato de Trabajadores de la Empresa Municipal de Agua y Saneamiento Ambiental (SITRAGUA) '
              },
              {
                video_id: '265556656',
                job: 'Emergency workers',
                image_slug: 'kumamoto',
                quote:
                  '“This is the town where I grew up, where I played - now all my memories have gone away”',
                title: 'Higher Ground',
                country: 'Japan',
                description:
                  'Japan faces up to hundreds of earthquakes a year and frontline workers are left to try and re-establish stability on shaky ground.',
                union: 'JICHIRO, JPSU'
              },
              {
                video_id: '265557487',
                name: 'Chi Chi',
                image_slug: 'chi',
                job: 'Midwife',
                quote:
                  '“It brings me such joy to hear the children of my village grow up to call me Mama Chi”',
                title: 'Mama Chi',
                country: 'The Philippines',
                description:
                  'Chi Chi, a Filipino midwife, shares her joy of working to bring new life into her local community.',
                union:
                  'Public Services Labor Independent Confederation (PSLINK)'
              }
            ]
          }
        },
        fr: {
          menu: [
            { name: 'A propos', url: '#about' },
            { name: 'Films', url: '#films' }
          ],
          intro: {
            series: 'Une série de films sur le monde du travail',
            sponsor:
              'Internationale des Services Publics<br />Fédération syndicale internationale'
          },
          people: [
            {
              content:
                'Au Tchad, Younous lutte non seulement pour sauver la vie de ses patient(e)s, mais également contre le régime dictatorial.',
              image: '/static/images/4.png'
            },
            {
              content:
                'Au Japon, Reina lutte contre les incendies et pour ses droits syndicaux.',
              image: '/static/images/5.png'
            },
            {
              content:
                'Au Liban, Georges se bat pour approvisionner sa communauté en eau, ainsi que pour les droits des réfugié(e)s.',
              image: '/static/images/6.png'
            }
          ],
          about: {
            lead:
              'Bien que leurs histoires soient différentes, ces travailleurs/euses des services publics servent un même objectif : améliorer la vie au sein de leur communauté, et pas seulement pour les plus riches.',
            content:
              '<p>L’Internationale des Services Publics, fédération syndicale internationale, a produit « One Day » (Un jour), une série de courts-métrages visant à représenter une journée ordinaire de la vie extraordinaire des travailleurs/euses des services publics comme Younous, Reina et Georges.</p> <p>Mettant en scène plus d’une dizaine de travailleurs/euses de première ligne aux quatre coins du monde, ces films illustrent la façon dont ils/elles luttent contre la privatisation, l’austérité et le néolibéralisme et mettent en avant les campagnes en faveur des droits syndicaux, de la préparation aux catastrophes et de l’accès à des services publics de qualité.</p>'
          },
          footer: {
            production: 'Produit par',
            shot: 'Filmé par',
            thanks: 'Remerciements particuliers à',
            art: 'Direction artistique',
            dev: 'Dévelopement',
            press: 'Demandes de presse',
            psi: 'Internationale des Services Publics, tous droits réservés.',
            psi_description:
              '<p><strong>L’Internationale des services publics (ISP)</strong> est une fédération syndicale internationale représentant 20 millions de femmes et d’hommes qui travaillent dans les services publics de 163 pays à travers le monde.</p>'
          },
          films: {
            title: 'Films',
            button: {
              label: 'Regarder le trailer',
              url: '265952877'
            },
            list: [
              {
                video_id: '265567469',
                name: 'Younous',
                name_slug: 'younous',
                image_slug: 'younous',
                job: 'Le docteur',
                quote:
                  "“C'est un système de torture appelé Arbatachar. C'est un système que vous n'oublierez pas “",
                title: 'Dr. Activiste',
                country: 'Tchad',
                description:
                  "Après avoir aidé à envoyer l'ancien dictateur du Tchad en prison, Younous maintient la lutte pour les droits syndicaux et les soins de santé pour tous dans l'un des régimes les plus répressifs d'Afrique.",
                union:
                  'Federation des Syndicats du Secteur Public du Tchad (FSPT)'
              },
              {
                video_id: '265569202',
                name: 'Arabella',
                name_slug: 'arabella',
                image_slug: 'arabella',
                job: 'Membre du personnel de la petite enfance',
                quote:
                  '“Les pleurs d’un enfant sont empreints d’un millier de nuances différentes.“',
                title: 'Sons et silence',
                country: 'Italie',
                description:
                  'Arabella nous offre un aperçu de la patience et de la sensibilité nécessaires pour former les plus jeunes membres de la société.',
                union:
                  'Confederazione Generale Italiana del Lavoro,  Funzione Pubblica'
              },
              {
                video_id: '265568159',
                name: 'Negro Davi',
                name_slug: 'negro',
                image_slug: 'negro',
                job: 'Travailleur des déchets et organisateur',
                quote:
                  '“Il y a une symbiose totale entre ma musique et mon activisme syndical”',
                title: 'Nous ne sommes pas des ordures',
                country: 'Brésil',
                description:
                  "À travers sa musique, Negro Davi défend le respect des travailleurs du secteur des déchets qu'il aide à organiser.",
                union: 'SINDILIMP'
              },
              {
                video_id: '265565081',
                name: 'Adjoujiu',
                name_slug: 'adjoujiu',
                image_slug: 'adjoujiu',
                job: 'Membre de l’administration publique',
                quote:
                  '“Lorsque nous essayons d’avancer, nous, les femmes, devons faire preuve d’une extrême prudence, car on nous met souvent des bâtons dans les roues. ”',
                title: 'Les femmes tchadiennes se mobilisent',
                country: 'Tchad',
                description:
                  'Suivez Adjoujiu dans son combat pour organiser la première Marche des femmes au Tchad, afin de défendre les droits des travailleurs/euses devant un régime oppressif.',
                union:
                  'Federation des Syndicats du Secteur Public du Tchad (FSPT)'
              },
              {
                video_id: '265564017',
                name: 'Chelsea',
                name_slug: 'chelsea',
                image_slug: 'chelsea',
                job: 'Agent de correction',
                quote:
                  '“Nous ne sommes pas tous méchants et durs et Grrrrrrr .... Nous ne sommes pas comme ça”',
                title: 'Montrant le visage humain',
                country: 'Etats-Unis',
                description:
                  "Face à l'opinion publique, Chelsea met en évidence les qualités humaines et le soin des autres requis pour son travail en tant qu'agent de la prison.",
                union: 'AFL-CIO'
              },
              {
                video_id: '265568915',
                name: 'Georges',
                name_slug: 'georges',
                image_slug: 'georges',
                job: "Travailleur de l'eau",
                quote:
                  '“Nous étions des réfugiés, nous avons vécu la guerre, et maintenant nous pouvons aider nos frères”',
                title: 'Solidarité à travers les frontières',
                country: 'Liban',
                description:
                  "Après que les réfugiés syriens ayant fui la guerre se soient installés dans la communauté où habite Georges, il travaille, avec son syndicat, pour s'assurer que les réfugiés ont tous accès à l'eau potable et à l'assainissement.",
                union:
                  'Syndicat des Employes et Ouvriers de l’Office des Eaux de Beyrouth et du Mt Liban (SEOEB)'
              },
              {
                video_id: '265568583',
                name: 'Eko',
                name_slug: 'eko',
                image_slug: 'eko',
                job: 'Travailleur du secteur de l’électricité',
                quote:
                  ' “Nous avons défendu les services publics d’électricité devant la Cour Suprême.”',
                title: "L'Énergie au peuple",
                country: 'Indonésie',
                description:
                  'Eko risque de perdre son emploi à l’usine locale d’électricité. Tandis que le gouvernement ouvre la voie à la privatisation, Eko et ses collègues ripostent.',
                union: 'Serikat Perkerja Perjuangan PT PLN Persero (SPP.PLN)'
              },
              {
                video_id: '265557112',
                name: 'Pasquale and Emmanuele',
                name_slug: 'pascale-emanuele',
                image_slug: 'pascale-emanuele',
                job: 'Travailleurs des déchets',
                quote:
                  '“Dans la matinée, les rues sont silencieuses, calmes, belles - nous les rendons propres”',
                title: 'Gardiens de la ville',
                country: 'Italie',
                description:
                  "Alors que les touristes et les résidents sont encore endormis, Pasquale et Emmauele sont déjà dans les rues pour aider à faire de Sienne l'une des plus belles villes du monde.",
                union:
                  'Confederazione Generale Italiana del Lavoro,  Funzione Pubblica'
              },
              {
                video_id: '265955585',
                name: 'Reina',
                name_slug: 'reina',
                image_slug: 'reina',
                job: 'Ambulance et pompier',
                quote:
                  "“En tant qu'enfant malade à l'hôpital, j'ai rêvé de grandir et d'aider les autres.”",
                title: 'Le rêve de Reina',
                country: 'Japon',
                description:
                  "Reina, l'une des quatre travailleuses d'urgence de son unité, lutte contre les incendies, sauve des vies et milite pour les droits des travailleurs dans la ville d'Omuta, au Japon.",
                union: 'JICHIRO, JPSU'
              },
              {
                video_id: '265564571',
                name: 'Demba',
                name_slug: 'demba',
                image_slug: 'demba',
                job: "L'aide juridique",
                quote:
                  '“Vous ne pouvez jamais laisser ce type de travail, au travail. Il reste avec toi”',
                title: 'Justice: un service public',
                country: 'Tchad',
                description:
                  "En tant qu'assistante juridique publique pour les victimes de viol et d'abus, Demba utilise la loi pour lutter contre les injustices - tout en luttant pour maintenir son espoir.",
                union:
                  'Federation des Syndicats du Secteur Public du Tchad (FSPT)'
              },
              {
                video_id: '265565551',
                name: 'Ali, Kamal and Elian',
                name_slug: 'ali-kamal-elian',
                image_slug: 'ali-kamal-elian',
                job: "Travailleurs de l'eau",
                quote:
                  "“Quand vous n'avez pas d'heures fixes, un contrat fixe - vous n'avez pas d'avenir”",
                title: "L'écoulement de l'eau",
                country: 'Liban',
                description:
                  "Les travailleurs de Tripoli luttent pour mettre fin à la contractualisation des jeunes camarades et étendre le service public de l'eau à tous.",
                union:
                  'Syndicat des Employes et Ouvriers de l’Office des Eaux de Beyrouth et du Mt Liban (SEOEB) '
              },
              {
                video_id: '265569455',
                name: 'Anvic',
                name_slug: 'anvic',
                image_slug: 'anvic',
                job: 'Premier/ère intervenant(e)',
                quote:
                  '“Nous devons investir et nous préparer à temps pour affronter l’avenir. ”',
                title: 'L’arche',
                country: 'Les Philippines',
                description:
                  'Lorsque les Philippines sont frappées par les catastrophes, Anvic fait partie des premiers/ères intervenant(e)s, ces personnes courageuses qui laissent leur famille derrière elles pour braver les dangers de la tempête.',
                union:
                  'Public Services Labor Independent Confederation (PSLINK)'
              },
              {
                video_id: '265565814',
                name: 'Daksim',
                name_slug: 'daksim',
                image_slug: 'daksim',
                job: "Travailleur de l'eau",
                quote:
                  "“Peu de gens le savent, mais si Jakarta n'obtient pas rapidement un système d'eau public efficace, la ville sombrera dans la mer.”",
                title: 'Le gros poisson',
                country: 'Indonésie',
                description:
                  "Après 20 ans de privatisation de l'eau, Daksim et son syndicat n'ont toujours pas renoncé à remettre leur approvisionnement en eau entre les mains de la population de Jakarta.",
                union:
                  'Serikat Pekerja Perusahaan Daerah Air Minum Jakarta (SP PDAM Jakarta)'
              },
              {
                video_id: '265563895',
                name: 'Chepito',
                name_slug: 'chepito',
                image_slug: 'chepito',
                job: "Travailleur de l'eau",
                quote:
                  "“Depuis mes études, ils battent. Cela ne m'a pas encore arrêté”",
                title: 'Camarade Chepito',
                country: 'Guatemala',
                description:
                  "Au Guatemala, où les syndicalistes font face à la détention et même à l'assassinat, Chepito travaille à l'organisation des travailleurs publics et se bat pour le droit à l'eau pour tous.",
                union:
                  'Sindicato de Trabajadores de la Empresa Municipal de Agua y Saneamiento Ambiental (SITRAGUA) '
              },
              {
                video_id: '265564264',
                image_slug: 'kumamoto',
                job: "Travailleurs d'urgence",
                quote:
                  "“C'est la ville où j'ai grandi, où j'ai joué - maintenant tous mes souvenirs sont partis”",
                title: 'Un terrain plus élevé',
                country: 'Japon',
                description:
                  'Le Japon fait face à des centaines de tremblements de terre par an et les travailleurs de première ligne doivent essayer de rétablir la stabilité sur un terrain instable.',
                union: 'JICHIRO, JPSU'
              },
              {
                video_id: '265566157',
                name: 'Chi Chi',
                image_slug: 'chi',
                job: 'Sage-femme',
                quote:
                  "“Cela me fait tellement plaisir d'entendre les enfants de mon village grandir et m'appeler Mama Chi”",
                title: 'Mama Chi',
                country: 'Les Philippines',
                description:
                  'Chi Chi, une sage-femme philippine, partage sa joie de travailler pour apporter une nouvelle vie dans sa communauté locale.',
                union:
                  'Public Services Labor Independent Confederation (PSLINK)'
              }
            ]
          }
        },
        es: {
          menu: [
            { name: 'Sobre', url: '#about' },
            { name: 'Films', url: '#films' }
          ],
          intro: {
            series: 'Una serie de cortometrajes sobre el mundo del trabajo ',
            sponsor:
              'Internacional de Servicios Públicos<br />Federación Sindical Internacional'
          },
          people: [
            {
              content:
                'En el Chad, Younous lucha por salvar la vida a sus pacientes y contra el régimen dictatorial.',
              image: '/static/images/4.png'
            },
            {
              content:
                'En Japón, Reina lucha contra los incendios y por su derecho a sindicalizarse.',
              image: '/static/images/5.png'
            },
            {
              content:
                'En el Líbano, Georges lucha por llevar agua a su comunidad y por los derechos de los refugiados.',
              image: '/static/images/6.png'
            }
          ],
          about: {
            lead:
              'Sus historias pueden ser diferentes, pero el objetivo de estos trabajadores y trabajadoras públicos es el mismo: mejorar sus comunidades en beneficio de todos, no sólo de un puñado de ricos.',
            content:
              '<p>One Day (Un día) es una serie de cortometrajes, producida por la federación sindical mundial Internacional de Servicios Públicos, que captan un día de trabajo ordinario en las extraordinarias vidas de los hombres y mujeres que trabajan en los servicios públicos, como Younous, Reina y Georges.</p> <p>En la serie aparecen más de una docena de trabajadores de países de todo el mundo. Estos cortometrajes nos muestran la primera línea de lucha contra la privatización, la austeridad y el neoliberalismo y las campañas que se libran en defensa de los derechos sindicales, de la planificación frente a catástrofes y del acceso a servicios públicos de calidad.</p>'
          },
          footer: {
            production: 'Producido por ',
            shot: 'Realizado por',
            thanks: 'Agradecimientos a',
            art: 'Dirección de Arte',
            dev: 'Desarrollo',
            press: 'Consultas de la prensa',
            psi:
              'Internacional de Servicios Públicos, todos los derechos reservados',
            psi_description:
              '<p><strong>La Internacional de Servicios Públicos</strong> es una federación sindical mundial que representa a 20 millones de trabajadores y trabajadoras que prestan servicios públicos esenciales en 163 países.</p>'
          },
          films: {
            title: 'Cortometrajes',
            button: {
              label: 'Ver el trailer',
              url: '265952877'
            },
            list: [
              {
                video_id: '265567469',
                name: 'Younous',
                name_slug: 'younous',
                image_slug: 'younous',
                job: 'Doctor',
                quote:
                  '“Es un sistema de tortura llamado Arbatachar. Es un sistema que no olvidas”',
                title: 'Dr. Activista',
                country: 'Chad',
                description:
                  'Después de ayudar a enviar al ex dictador de Chad a prisión, Younous continúa la lucha por los derechos sindicales y la atención médica para todos en uno de los regímenes más represivos de África.',
                union:
                  'Federation des Syndicats du Secteur Public du Tchad (FSPT)'
              },
              {
                video_id: '265561823',
                name: 'Arabella',
                name_slug: 'arabella',
                image_slug: 'arabella',
                job: 'Personal de educación infantil',
                quote: '“El llanto de un niño tiene mil matices diferentes”',
                title: 'Sonido y silencio',
                country: 'Italia',
                description:
                  'Arabella nos da una idea de la paciencia y la sensibilidad necesarias para ayudar al desarrollo de los miembros más jóvenes de nuestra sociedad.',
                union:
                  'Confederazione Generale Italiana del Lavoro,  Funzione Pubblica'
              },
              {
                video_id: '265563544',
                name: 'Negro Davi',
                name_slug: 'negro',
                image_slug: 'negro',
                job: 'Trabajador y organizador de desechos',
                quote:
                  '“Hay una simbiosis total entre mi música y mi activismo sindical”',
                title: 'No somos basura',
                country: 'Brasil',
                description:
                  'A través de su música, Negro Davi defiende el respeto por los trabajadores de residuos que el ayuda a organizar.',
                union: 'SINDILIMP'
              },
              {
                video_id: '265561070',
                name: 'Adjoujiu',
                name_slug: 'adjoujiu',
                image_slug: 'adjoujiu',
                job: 'Administradora pública',
                quote:
                  '“Cuando las mujeres tratamos de salir adelante, nos suelen colocar cáscaras de plátano delante, para hacernos caer”',
                title: 'Las mujeres de Chad marchan',
                country: 'Chad',
                description:
                  'Conoce la lucha que libra Adjoujiu para organizar la primera Marcha de las Mujeres en el Chad por los derechos de los trabajadores, frente a un régimen opresivo.',
                union:
                  'Federation des Syndicats du Secteur Public du Tchad (FSPT)'
              },
              {
                video_id: '265561481',
                name: 'Chelsea',
                name_slug: 'chelsea',
                image_slug: 'chelsea',
                job: 'Funcionaria de prisiones',
                quote:
                  '“No somos todos malos y rudos y ¡Grrrrrrr! .... Simplemente no somos así ”',
                title: 'Mostrando rostro humano',
                country: 'Estados Unidos',
                description:
                  'Ante la opinión pública, Chelsea destaca las cualidades humanas y el cuidado de los demás necesarios para su trabajo como oficial de prisiones.',
                union: 'AFL-CIO'
              },
              {
                video_id: '265562815',
                name: 'Georges',
                name_slug: 'georges',
                image_slug: 'georges',
                job: 'Trabajador de agua',
                quote:
                  '“Fuimos refugiados, vivimos la guerra y ahora podemos ayudar a nuestros hermanos”',
                title: 'Solidaridad a través de las fronteras',
                country: 'Líbano',
                description:
                  'Después de que los refugiados sirios que huyeron la guerra se instalaron en la comunidad de Georges, él y su sindicato trabajan para garantizar que todos tengan acceso a agua potable y saneamiento.',
                union:
                  'Syndicat des Employes et Ouvriers de l’Office des Eaux de Beyrouth et du Mt Liban (SEOEB)'
              },
              {
                video_id: '265562507',
                name: 'Eko',
                name_slug: 'eko',
                image_slug: 'eko',
                job: 'Empleado del sector de la energía',
                quote:
                  ' “Llevamos hasta la Corte Suprema nuestro caso en defensa de la electricidad pública”',
                title: 'Poder para el pueblo',
                country: 'Indonesia',
                description:
                  'Eko trabaja en la central eléctrica local y su empleo está en la cuerda floja. Mientras el Gobierno se prepara para la privatización, él y sus colegas se defienden.',
                union: 'Serikat Perkerja Perjuangan PT PLN Persero (SPP.PLN)'
              },
              {
                video_id: '265563027',
                name: 'Pasquale and Emmanuele',
                name_slug: 'pascale-emanuele',
                image_slug: 'pascale-emanuele',
                job: 'Trabajadores de residuos',
                quote:
                  '“Por la mañana, las calles son silenciosas, tranquilas, hermosas; las limpiamos”',
                title: 'Guardianes de la ciudad',
                country: 'Italia',
                description:
                  'Mientras los turistas y los residentes aún duermen, Pasquale y Emmauele están en las calles para ayudar a mantener a Siena como una de las ciudades más bellas del mundo.',
                union:
                  'Confederazione Generale Italiana del Lavoro,  Funzione Pubblica'
              },
              {
                video_id: '265955585',
                name: 'Reina',
                name_slug: 'reina',
                image_slug: 'reina',
                job: 'Trabajadora de servicio de bomberos y ambulancias',
                quote:
                  '“Como un niño enfermo en el hospital, soñé con crecer y ayudar a los demás”',
                title: 'Sueño de Reina',
                country: 'Japón',
                description:
                  'Como una de las cuatro mujeres trabajadoras de emergencias en su unidad, Reina lucha contra los incendios, salva vidas y aboga por los derechos de los trabajadores en la ciudad de Omuta, Japón.',
                union: 'JICHIRO, JPSU'
              },
              {
                video_id: '265562287',
                name: 'Demba',
                name_slug: 'demba',
                image_slug: 'demba',
                job: 'Asistencia legal',
                quote:
                  '“Nunca puedes dejar este tipo de trabajo, en el trabajo. Se queda contigo”',
                title: 'Justicia: un servicio público',
                country: 'Chad',
                description:
                  'Como asistente legal público para las víctimas de violación y abuso, Demba usa la ley para luchar contra las injusticias, mientras lucha para mantener su esperanza.',
                union:
                  'Federation des Syndicats du Secteur Public du Tchad (FSPT)'
              },
              {
                video_id: '265560592',
                name: 'Ali, Kamal and Elian',
                name_slug: 'ali-kamal-elian',
                image_slug: 'ali-kamal-elian',
                job: 'Trabajadorxs del agua',
                quote:
                  '“Cuando no tienes un horario fijo, un contrato fijo, no tienes futuro”',
                title: 'Flujo de agua',
                country: 'Líbano',
                description:
                  'Los trabajadores en Trípoli luchan para terminar la contractualización de los camaradas jóvenes y extender el servicio público de agua a todos.',
                union:
                  'Syndicat des Employes et Ouvriers de l’Office des Eaux de Beyrouth et du Mt Liban (SEOEB) '
              },
              {
                video_id: '265561252',
                quote:
                  '“Cuando todo está en calma es el momento de invertir y planificar para lo que viene después”',
                title: 'El Arca',
                name_slug: 'anvic',
                image_slug: 'anvic',
                country: 'Filipinas',
                description:
                  'Cuando ocurre una catástrofe en Filipinas, Anvic es uno de los valientes del personal de primera intervención que deja atrás a su familia y corre a adentrarse en el ojo del huracán.',
                union:
                  'Public Services Labor Independent Confederation (PSLINK)'
              },
              {
                video_id: '265562065',
                name: 'Daksim',
                name_slug: 'daksim',
                image_slug: 'daksim',
                job: 'Trabajador de agua',
                quote:
                  '“No mucha gente lo sabe, pero si no conseguimos un sistema de agua pública eficaz pronto, Yakarta se hundirá en el mar”',
                title: 'El pez grande',
                country: 'Indonesia',
                description:
                  'Después de 20 años de privatización fallida del agua, Daksim y su sindicato aún no han renunciado a la lucha para devolver su suministro de agua a las manos de la gente de Yakarta.',
                union:
                  'Serikat Pekerja Perusahaan Daerah Air Minum Jakarta (SP PDAM Jakarta)'
              },
              {
                video_id: '265560786',
                name: 'Chepito',
                name_slug: 'chepito',
                image_slug: 'chepito',
                job: 'Trabajador de agua',
                quote:
                  '“Desde mis días como estudiante, han golpeado. No me ha detenido todavía ”',
                title: 'Camarada Chepito',
                country: 'Guatemala',
                description:
                  'En Guatemala, donde los sindicalistas enfrentan detenciones e incluso asesinatos, Chepito trabaja para organizar a los trabajadores públicos y lucha por el derecho al agua para todos.',
                union:
                  'Sindicato de Trabajadores de la Empresa Municipal de Agua y Saneamiento Ambiental (SITRAGUA) '
              },
              {
                video_id: '265564264',
                image_slug: 'kumamoto',
                job: 'Trabajadores de emergencia',
                quote:
                  '“Esta es la ciudad donde crecí, donde jugué, ahora todos mis recuerdos se han ido”',
                title: 'Tierra más alta',
                country: 'Japón',
                description:
                  'Japón se enfrenta a cientos de terremotos al año y los trabajadores de primera línea deben intentar restaurar la estabilidad en terreno inestable.',
                union: 'JICHIRO, JPSU'
              },
              {
                video_id: '265561708',
                name: 'Chi Chi',
                image_slug: 'chi',
                job: 'Partera',
                quote:
                  '“Me produce tanta alegría escuchar a los niños de mi pueblo crecer y llamarme Mamá Chi”',
                title: 'Mama Chi',
                country: 'Filipinas',
                description:
                  'Chi Chi, una partera filipina, comparte su alegría de trabajar para traer nueva vida a su comunidad local.',
                union:
                  'Public Services Labor Independent Confederation (PSLINK)'
              }
            ]
          }
        }
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Home.vue